/**
 * 顧客データ一括更新コンポーネント
 *
 * このコンポーネントは、CSVまたはXLSX形式のファイルをアップロードし、
 * 顧客データを一括更新する機能を提供します。
 * ファイルのフォーマットチェック、データの解析、更新処理を行い、
 * 更新結果を表示します。
 *
 * 主な機能:
 * - サンプルファイルのダウンロード
 * - ファイルアップロードとフォーマットチェック
 * - 更新処理の実行
 * - 更新結果とエラー詳細の表示
 */
import React, { useRef, useState } from 'react';
import { Flex, Upload, message, Button, Modal, List, Typography } from 'antd';
import { InboxOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { DownloadButton } from 'src/components/ui';
import { CSVUpdateData, BulkUpdateResult } from 'src/usecases/mCustomer/usecase';
import { csvToArray } from 'src/util/array';
import { download } from 'src/util/download';
const { Text } = Typography;

interface CsvBulkUpdateProps {
  onUpdate: (data: CSVUpdateData[]) => Promise<BulkUpdateResult>;
  onUpdatingChange: (updating: boolean) => void;
}

export const CsvBulkUpdate: React.FC<CsvBulkUpdateProps> = ({
  onUpdate,
  onUpdatingChange,
}) => {
  // CSVアップロード用の状態
  const [csvUpdateData, setCsvUpdateData] = useState<CSVUpdateData[]>([]);
  // ファイル読み込み中の状態
  const [processing, setProcessing] = useState(false);
  
  // エラーモーダル用の状態
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [updateResult, setUpdateResult] = useState<BulkUpdateResult | null>(null);

  /**
   * サンプルファイルをダウンロードする関数
   *
   * @param format - ダウンロードするファイル形式 ('csv' または 'xlsx')
   * @returns void
   */
  const downloadSample = async (format: 'csv' | 'xlsx') => {
    download(
      [
        ['LINE_ID', 'ニックネーム', 'コメント'],
        ['U716f5f7517de3aadglfg2c7d83055910', '新しいニックネーム1', '追加するコメント内容1'],
        ['Uff64d02382d4asd7c825c5e7c30c20e0', '新しいニックネーム2', '追加するコメント内容2'],
      ],
      '顧客更新サンプルデータ',
      format
    );
  };

  /**
   * ファイルアップロード処理を行う関数
   *
   * @param file - アップロードされたファイル
   * @returns Promise<boolean> - 処理結果を示すPromise
   *
   * この関数は、ファイルのフォーマットチェック、データ解析、
   * 必須列の確認、データ行の解析を行います。
   * 処理が成功した場合、解析されたデータを状態に保存します。
   */
  const processFileUpload = (file: File): Promise<boolean> => {
    setProcessing(true);
    
    const isCSV = file.type === 'text/csv';
    
    if (!isCSV) {
      message.error('csvファイルのみアップロード可能です');
      setProcessing(false);
      return Promise.resolve(false);
    }

    return new Promise<boolean>((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          if (!e?.target?.result || typeof e?.target?.result !== 'string') {
            message.error('フォーマットが無効です');
            resolve(false);
            return;
          }

          const _csv = csvToArray(e.target.result.toString());
          const headers = _csv[0];

          // 列名の重複チェック
          const headersToValidate = ['LINE_ID', 'ニックネーム', 'コメント'];
          for (const headerName of headersToValidate) {
            if (headers.filter(h => h === headerName).length > 1) {
              message.error(`${headerName}列が複数存在します`);
              resolve(false);
              return;
            }
          }

          // 必須列のチェック
          const lineIdIndex = headers.findIndex(header => header === 'LINE_ID');
          if (lineIdIndex === -1) {
            message.error('フォーマットが無効です。LINE_IDは必須です。');
            resolve(false);
            return;
          }

          if (_csv.length < 2) {
            message.error('データが含まれていません');
            resolve(false);
            return;
          }

          // その他の列のインデックスを取得
          const nicknameIndex = headers.indexOf('ニックネーム');
          const remarksIndex = headers.indexOf('コメント');

          // データ行を解析
          const updateData: CSVUpdateData[] = [];
          
          for (let i = 1; i < _csv.length; i++) {
            const row = _csv[i];
            const lineId = row[lineIdIndex]?.trim();
            
            // LINE_IDが空の行はスキップ
            if (!lineId) {
              continue;
            }

            const nickname = nicknameIndex !== -1 ? (row[nicknameIndex]?.trim() || '') : '';
            const remarks = remarksIndex !== -1 ? (row[remarksIndex]?.trim() || '') : '';

            updateData.push({
              lineId,
              nickname,
              remarks,
            });
          }

          if (updateData.length === 0) {
            message.error('LINE_IDが設定されているデータがありませんでした。');
            resolve(false);
            return;
          }

          if (updateData.length > 500) {
            message.error('一度に更新できるのは最大500件までです');
            resolve(false);
            return;
          }

          // 成功時にデータを保存
          setCsvUpdateData(updateData);
          message.success(`${updateData.length}件のデータを読み込みました`);
          resolve(false);
          
        } catch (error) {
          message.error('ファイルの解析中にエラーが発生しました');
          resolve(false);
        } finally {
          setProcessing(false);
        }
      };

      reader.onerror = () => {
        message.error('ファイルの読み取りに失敗しました');
        setProcessing(false);
        resolve(false);
      };

      reader.readAsText(file, 'UTF-8');
    });
  };

  // ファイルアップロード設定
  const uploadProps: UploadProps = {
    accept: '.csv',
    beforeUpload: processFileUpload,
    onRemove: () => {
      setCsvUpdateData([]);
    },
    showUploadList: false,
  };

  /**
   * 顧客データの一括更新を実行する関数
   *
   * @returns void
   *
   * この関数は、解析されたデータを基に更新処理を実行し、
   * 更新結果を表示します。エラーが発生した場合は詳細をモーダルで表示します。
   */
  const executeUpdate = async () => { 
    if (csvUpdateData.length === 0) {
      message.warning('更新するデータがありません');
      return;
    }

    onUpdatingChange(true);
    try {
      // 実際のFirestore更新処理
      const result = await onUpdate(csvUpdateData);
      
      // 結果を保存
      setUpdateResult(result);

      // 結果に基づいてメッセージを表示
      if (result.success > 0 && result.failed === 0) {
        message.success(`${result.success}件のデータを更新しました`);
      } 
      else if (result.success > 0 && result.failed > 0) {
        message.warning(`${result.success}件が成功、${result.failed}件が失敗しました`);
      } 
      else if (result.failed > 0) {
        message.error(`${result.failed}件の更新に失敗しました`);
      }

      // エラー詳細の表示判定
      if (result.errors.length > 0) {
        // エラーがある場合は件数のみ表示し、詳細はモーダルで確認
        message.warning(`${result.errors.length}件のエラー詳細があります。下記ボタンから確認してください。`);
      }
      
      // データクリア
      setCsvUpdateData([]);
    } 
    catch (error) {
      setUpdateResult({
        success: 0,
        failed: csvUpdateData.length,
        errors: ['更新中に予期せぬエラーが発生しました'],
        updatedMCustomerIds: []
      });
      message.error('更新中にエラーが発生しました。しばらく時間をおいて再度お試しください。');
    } 
    finally {
      onUpdatingChange(false);
    }
  };

  /**
   * エラー詳細モーダルを表示する関数
   *
   * @returns void
   */
  const showErrorModal = () => {
    setErrorModalVisible(true);
  };

  /**
   * エラー詳細モーダルを閉じる関数
   *
   * @returns void
   */
  const handleErrorModalClose = () => {
    setErrorModalVisible(false);
  };

  return (
    <>
      <Flex
        style={{
          background: 'white',
          borderRadius: 3,
          padding: '2%',
        }}
        vertical
        align="flex-start"
      >
        <h3 style={{ padding: '0%' }}>顧客データ一括更新</h3>
        <div style={{ height: '10px' }}></div>
        
        <DownloadButton
          name="サンプルダウンロード"
          tooltip="顧客データ更新用のサンプルファイルをダウンロードできます"
          onClick={downloadSample}
          type="primary"
        />

        <div style={{ height: '10px' }}></div>

        <Upload.Dragger 
          {...uploadProps} 
          style={{ maxWidth: '400px' }}
          disabled={processing} // 読み込み中は無効化
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            クリックまたはファイルをドラッグ
          </p>
          <p 
            className="ant-upload-hint"
            style={{ whiteSpace: 'pre-wrap', textAlign: 'start' }}
          >
            {`※以下の列に対応しています：\n・LINE_ID（必須）：更新対象のLINE ID\n・ニックネーム（任意）：新しいニックネーム（空欄の場合は既存値維持）\n・コメント（任意）：既存コメントに追記する内容（空欄の場合は追記しない）\n※最大500ユーザとなります\n※csv形式に対応しています`}
          </p>
        </Upload.Dragger>

        {csvUpdateData.length > 0 && (
          <Flex gap="small" align="center" style={{ marginTop: '10px' }}>
            <span style={{ color: '#666' }}>
              読み込み済み: {csvUpdateData.length}件
            </span>
            <Button
              type="primary"
              onClick={executeUpdate}
              disabled={processing}
            >
              一括更新実行
            </Button>
          </Flex>
        )}

        {updateResult && updateResult.errors.length > 0 && (
          <Flex gap="small" align="center" style={{ marginTop: '10px' }}>
            <Text type="warning">
              <ExclamationCircleOutlined /> {updateResult.errors.length}件のエラーがあります
            </Text>
            <Button 
              type="link" 
              onClick={showErrorModal}
              size="small"
            >
              詳細を表示
            </Button>
          </Flex>
        )}
      </Flex>

      <Modal
        title="更新エラー詳細"
        open={errorModalVisible}
        onCancel={handleErrorModalClose}
        footer={[
          <Button key="close" onClick={handleErrorModalClose}>
            閉じる
          </Button>
        ]}
        width={600}
      >
        {updateResult && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Text strong>更新結果: </Text>
              <Text type="success">成功 {updateResult.success}件</Text>
              <Text>, </Text>
              <Text type="danger">失敗 {updateResult.failed}件</Text>
            </div>
            
            <div>
              <Text strong>エラー詳細:</Text>
              <List
                style={{ marginTop: '8px' }}
                size="small"
                bordered
                dataSource={updateResult.errors}
                renderItem={(error, index) => (
                  <List.Item>
                    <Text type="danger">{index + 1}. {error}</Text>
                  </List.Item>
                )}
                pagination={updateResult.errors.length > 10 ? {
                  pageSize: 10,
                  size: 'small'
                } : false}
              />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};
